import { useQuery } from "@tanstack/react-query";

// Native CLMM tick-liquidity snapshot (the /dex-tick-liquidity/live-profile
// endpoint). Distinct from useLiveProfile, which reads the pre-simulated
// bps-band depth out of book_l2; this is the pool's actual initialized-tick
// liquidity distribution, valued in USD at read time via reference_prices.
// >int64 fields arrive as strings (exact); token amounts and USD arrive as
// numbers (render units).

export interface DexTick {
  tick_index: number;
  price_lower: number | null;
  price_upper: number | null;
  active_liquidity: string;
  liquidity_net: string;
  liquidity_gross: string;
  distance_bps: number | null;
  amount_base: number | null;   // base-token units held in this range (e.g. SAFE)
  amount_quote: number | null;  // quote-token units held in this range (e.g. WETH)
  value_usd: number | null;     // (amount_quote + amount_base*price) * eth_usd
  next_tick: number | null;
  bps_lower: number | null;     // distance of tick_index from current price
  bps_upper: number | null;     // distance of next initialized tick
  is_active: boolean;           // tick_index <= current_tick < next_tick (authoritative)
  is_anchor: boolean;           // audit only
}

export interface DexTickLiquidity {
  venue: string;
  pair: string;
  ts: string | null;
  block_number: number | null;
  current_tick: number | null;
  current_price: number | null;
  active_liquidity: string | null;
  tick_spacing: number | null;
  active_tick: number | null;
  anchor_tick: number | null;   // audit only
  anchor_ok: boolean | null;
  usd_per_base: number | null;  // current price in USD per base (e.g. USD/SAFE)
  base_per_quote: number | null;// 1/current_price (e.g. SAFE per WETH)
  eth_usd: number | null;             // ETH/USD used for conversion
  eth_usd_ts: string | null;          // timestamp of that rate
  eth_usd_age_seconds: number | null;
  quote_usd_rate?: number | null;   // quote asset's USD price (WETH->ETH/USD, USDC->1.0)
  quote_asset?: string | null;
  tick_count: number;
  status: string;
  profile_source?: string | null;
  ticks: DexTick[];
}

async function fetchDexTickLiquidity(
  venue: string,
  pair: string,
): Promise<DexTickLiquidity> {
  const qs = new URLSearchParams({ venue, pair });
  const res = await fetch(
    `/api/v1/dex-tick-liquidity/live-profile?${qs.toString()}`,
    { headers: { Accept: "application/json" } },
  );
  if (!res.ok) throw new Error(`dex-tick-liquidity ${res.status}`);
  return (await res.json()) as DexTickLiquidity;
}

// Tick snapshots land ~every 60s; poll at 30s so a new snapshot shows promptly.
const POLL_MS = 30_000;

export function useDexTickLiquidity(venue: string, pair: string) {
  return useQuery({
    queryKey: ["dex-tick-liquidity", venue, pair],
    queryFn: () => fetchDexTickLiquidity(venue, pair),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS,
  });
}
